import React from 'react'
import store, { persistor } from './store/store'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import AuthWrapper from './components/other/AuthWrapper'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import Home from './components/organisms/Home'
import './i18n/i18n'
import Sell from './components/organisms/Sell'
import Clients from './components/organisms/Clients'
import ClientEdit from './components/organisms/ClientEdit'
import ClientCreate from './components/organisms/ClientCreate'
import ClientDetails from './components/organisms/ClientDetails'
import InvoiceCreate from './components/organisms/InvoiceCreate'
import MotorbikeCreate from './components/organisms/MotorbikeCreate'
import MotorbikeDetails from './components/organisms/MotorbikeDetails'
import PromotionCreate from './components/organisms/PromotionCreate'
import PromotionEdit from './components/organisms/PromotionEdit'
import MotorbikeEdit from './components/organisms/MotorbikeEdit'
import SellContractCreate from './components/organisms/SellContractCreate'
import SellContractUpdate from './components/organisms/SellContractUpdate'
import ImportDocumentCreate from './components/organisms/ImportDocumentCreate'
import ImportDocumentUpdate from './components/organisms/ImportDocumentUpdate'
import SellingInvoices from './components/organisms/SellingInvoices'
import Vehicles from './components/organisms/Vehicles'
import Purchase from './components/organisms/Purchase'
import Offer from './components/organisms/Offer'
import Liked from './components/organisms/Liked'
import WebSocketConnector from './helpers/WebSocketConnector'
import ResetPassword from './components/organisms/ResetPassword'
import NewPassword from './components/organisms/NewPassword'
import Conversations from './components/organisms/Conversations'
import Conversation from './components/organisms/Conversation'
import Page404 from './components/organisms/Page404'
import Callback from './components/organisms/Callback'
import DealerOffers from './components/organisms/DealerOffers'
import Realization from './components/organisms/Realization'
import Archived from './components/organisms/Archived'
import ArchivedOnPlatform from './components/organisms/ArchivedOnPlatform'
import Invoices from './components/organisms/Invoices'
import LanguageSelector from './components/other/LanguageSelector'
import ProfileSettings from './components/organisms/ProfileSettings'
import Integrations from './components/organisms/Integrations'
import LanguageSettings from './components/organisms/LanguageSettings'
import UpdatePasswordSetting from './components/organisms/UpdatePasswordSettings'
import OfferFetcher from './components/atoms/OfferFetcher'
import MobileRedirect from './components/atoms/MobileRedirect'
import BrowseWrapper from './components/atoms/BrowseWrapper'
import AppStateRefetch from './components/organisms/AppStateRefetch'
import BlockedAppModal from './components/molecules/BlockedAppModal'
import InvoiceUpdate from './components/organisms/InvoiceUpdate'
import DemoModal from './components/molecules/DemoModal'
import PhoneConfirmationModal from './components/molecules/PhoneConfirmationModal'
import TestAccessBanner from './components/atoms/TestAccessBanner'
import ErrorBoundary from './components/other/ErrorBoundary'
import CaptchaGate from './components/other/CaptchaGate'
import ToastHost, { ToastNavigateBridge } from './components/molecules/ToastHost'

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <CaptchaGate>
            <>
              <WebSocketConnector />
              <LanguageSelector />
              <OfferFetcher />
              <AppStateRefetch />
              <BlockedAppModal />
              <TestAccessBanner />
              <DemoModal />
              <PhoneConfirmationModal />
              <ToastHost />

              <BrowserRouter>
                <ToastNavigateBridge />
                <AuthWrapper>
                  <>
                    <Routes>
                      <Route index path="/motorbikes" element={<Home estibrand="estimoto" />} />
                      <Route index path="/campers" element={<Home estibrand="esticampo" />} />
                      <Route index path="/agricultural-machines" element={<Home estibrand="estiagro" />} />
                      <Route index path="/" element={<Home estibrand="estimoto" />} />

                      {/* <Route path="browsing/browse" element={<Browse />} /> */}
                      <Route path="browsing/liked" element={<Liked />} />
                      <Route path="browsing/offer/:id" element={<Offer />} />
                      <Route path="purchasing/purchase" element={<Purchase />} />
                      <Route path="purchasing/conversations" element={<Conversations />} />
                      <Route path="purchasing/conversations/:offer_hash_id" element={<Conversation />} />
                      <Route path="purchasing/my-bets" element={<DealerOffers />} />
                      <Route path="purchasing/realization" element={<Realization />} />
                      <Route path="purchasing/archived" element={<Archived />} />
                      <Route path="settings/profile" element={<ProfileSettings />} />
                      <Route path="selling/integrations" element={<Integrations />} />
                      <Route path="settings/language" element={<LanguageSettings />} />
                      <Route path="settings/password" element={<UpdatePasswordSetting />} />
                      <Route path="selling/sell" element={<Sell />} />
                      <Route path="selling/vehicles" element={<Vehicles />} />
                      <Route path="selling/vehicles/new" element={<MotorbikeCreate />} />
                      <Route path="selling/vehicles/:id/edit" element={<MotorbikeEdit />} />
                      <Route path="selling/vehicles/:id/promotions/new" element={<PromotionCreate />} />
                      <Route path="selling/vehicles/:id/promotions/:promotionId/edit" element={<PromotionEdit />} />
                      <Route path="selling/vehicles/:id" element={<MotorbikeDetails />} />
                      <Route path="selling/clients" element={<Clients />} />
                      <Route path="selling/clients/create" element={<ClientCreate />} />
                      <Route path="selling/clients/:id" element={<ClientDetails />} />
                      <Route path="selling/clients/:id/edit" element={<ClientEdit />} />
                      <Route path="selling/invoices/create" element={<InvoiceCreate />} />
                      <Route path="selling/sell-contracts/create" element={<SellContractCreate />} />
                      <Route path="selling/import-documents/create" element={<ImportDocumentCreate />} />
                      <Route path="selling/clients/:clientId/invoices/create" element={<InvoiceCreate />} />
                      <Route path="selling/clients/:clientId/invoices/:invoiceId/edit" element={<InvoiceUpdate />} />
                      <Route path="selling/clients/:clientId/sell-contracts/create" element={<SellContractCreate />} />
                      <Route
                        path="selling/clients/:clientId/sell-contracts/:sellContractId/edit"
                        element={<SellContractUpdate />}
                      />
                      <Route
                        path="selling/clients/:clientId/import-documents/create"
                        element={<ImportDocumentCreate />}
                      />
                      <Route
                        path="selling/clients/:clientId/import-documents/:importDocumentId/edit"
                        element={<ImportDocumentUpdate />}
                      />
                      <Route path="selling/invoices" element={<SellingInvoices />} />
                      <Route path="purchasing/archived" element={<Archived />} />
                      <Route path="purchasing/invoices" element={<Invoices />} />
                      <Route path="archived-on-platform" element={<ArchivedOnPlatform />} />
                      <Route path="reset-password" element={<ResetPassword />} />
                      <Route path="new-password/:token/:email" element={<NewPassword />} />
                      <Route path="auth/callback" element={<Callback />} />
                      <Route path="*" element={<Page404 />} />
                    </Routes>
                    <BrowseWrapper />
                    <MobileRedirect />
                  </>
                </AuthWrapper>
              </BrowserRouter>
            </>
          </CaptchaGate>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  )
}

export default App
